import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useSnapshot } from "valtio";
import { wizardStore } from "../../../stores/wizard";
import { CircleData, Step } from "../../../types/wizard";
import SelectedColor from "../../Wizard/commonComps/ColorPicker/SelectedColor";
import ColorManager from "./ColorManager";
import { DeleteIcon, EditIcon } from "../../../utils/icons";
import './../QuickView.css';

type TooltipProps = {
  isEditing: boolean;
  stepColorId: string;
  targets: CircleData[];
  indications?: CircleData[];
  containerRef: any;
  position: { x: number; y: number };
  onRemove: (matchedCircle: CircleData) => void;
  onEdit: (matchedCircle: CircleData) => void;
};

const TOOLTIP_WIDTH = 380;
const TOOLTIP_HEIGHT = 150;
const OFFSET = 12;

const ColorTooltip = forwardRef<HTMLDivElement, TooltipProps>(
  ({
    isEditing,
    stepColorId,
    targets,
    indications,
    position,
    containerRef,
    onRemove,
    onEdit
  }, ref
  ) => {
    const wizSnap = useSnapshot(wizardStore);
    const { steps } = wizSnap;
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [tooltipPosition, setTooltipPosition] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
    const [isPositioned, setIsPositioned] = useState(false);

    useImperativeHandle(ref, () => tooltipRef.current as HTMLDivElement);

    useEffect(() => {
      const updateTooltipPosition = () => {

        if (!containerRef.current) return;
        const containerRect = containerRef.current.getBoundingClientRect();
        const absoluteX = containerRect.left + position.x + window.scrollX;
        const absoluteY = containerRect.top + position.y + window.scrollY;

        let left = absoluteX + OFFSET;
        let top = absoluteY + OFFSET;

        if (left + TOOLTIP_WIDTH > window.innerWidth + window.scrollX) {
          left = absoluteX - TOOLTIP_WIDTH - OFFSET;
        }
        if (top + TOOLTIP_HEIGHT > window.innerHeight + window.scrollY) {
          top = absoluteY - TOOLTIP_HEIGHT - OFFSET;
        }

        setTooltipPosition({ left, top });
        setIsPositioned(true);
      };

      updateTooltipPosition();
      window.addEventListener("resize", updateTooltipPosition);
      window.addEventListener("scroll", updateTooltipPosition);

      return () => {
        window.removeEventListener("resize", updateTooltipPosition);
        window.removeEventListener("scroll", updateTooltipPosition);
      };
    }, [position, containerRef]);

    const matchedCircle = useMemo(() => targets?.find(t => t.circle?.x === position.x && t.circle?.y === position.y), [targets, position]);
    const findIndication = indications?.find(i => i?.circle?.id === matchedCircle?.circle?.id);
    const step = steps?.find(s => s.id === stepColorId) as Step;

    if (!isPositioned || !tooltipPosition) return null;

    const tooltipContent = (
      <div
        ref={tooltipRef}
        className={`absolute duration-[325ms] z-10 bg-white text-black text-sm p-2 rounded box-shadow transition-all duration-200 ease-out
        ${isPositioned ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}
        style={{
          left: tooltipPosition.left + 20,
          top: tooltipPosition.top,
          width: matchedCircle ? '250px' : '380px'
        }}
      >
        {matchedCircle && !isEditing ? (
          <>
            <div className="gap-2">
              <button
                onClick={(event) => { event.stopPropagation(); onRemove(matchedCircle) }}
                className="absolute top-0 right-1 bg-white outline-none border-0 hover:outline-none focus:outline-none px-1 pb-0">
                <DeleteIcon />
              </button>
            </div>
            <div className="gap-2">
              <button
                onClick={(event) => { event.stopPropagation(); onEdit(matchedCircle) }}
                className="absolute top-0 right-8 bg-white outline-none border-0 hover:outline-none focus:outline-none px-1 pb-0">
                <EditIcon />
              </button>
            </div>
            <div className="">
              <SelectedColor color={matchedCircle.option} />
              {findIndication &&
                <div className="flex flex-col gap-1 pt-2">
                  <h3 className="font-bold">{findIndication.dimension?.toUpperCase()} DIMENSION</h3>
                  <span className="">{findIndication.instructions}</span>
                </div>
              }
            </div>
          </>
        ) : (
          <React.Fragment>
            <ColorManager step={step} isEditing={isEditing} />
          </React.Fragment>
        )}
      </div>

    );

    return ReactDOM.createPortal(tooltipContent, document.body);
  }
);

export default ColorTooltip;